import { IconBin, IconHeart, IconHeartFilled } from "@tine/designsystem-icons/sharp";
import { Button, Group, InputStepper, Stack } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import clamp from "lodash-es/clamp";
import defaultTo from "lodash-es/defaultTo";
import isUndefined from "lodash-es/isUndefined";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "wouter";
import AddToFavoriteModal from "../../account/favorites/AddToFavoriteModal";
import { logger } from "../../common/logger";
import { sendAddCartToFavoriteList, sendRemoveFromCart } from "../../common/tracking";
import type { CartItem, Product } from "../../common/types/productTypes";
import { type GAListInformation, LIST_TYPE } from "../../common/types/trackingTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { formatPrice, formatUnit, inRangeInclusive, mobileBreakpoint } from "../../common/utils";
import ProductImage from "../../components/common/product/ProductImage";
import DiscountLabel from "../../components/DiscountLabel";
import DisplayPrice from "../../components/DisplayPrice";
import Divider from "../../components/Divider";
import { LabelComponent } from "../../components/Labels";
import LeadTimeWarning from "../../components/LeadTimeWarning";
import cartStore from "../../stores/cart/cartStore";
import cartTrackingStore from "../../stores/cart/cartTrackingStore";
import productStore from "../../stores/product/productStore";
import { isDistributionPack } from "../../stores/product/productUtils";
import promotionStore from "../../stores/promotionStore";
import toastStore from "../../stores/toastStore";

type RecyclingChangeProps = {
   product: Product;
};

const listStyle = "divide-x-0 divide-y divide-ink-border-subtle divide-solid list-none ps-0 w-full";

const RecyclingChange = observer(({ product }: RecyclingChangeProps) => {
   if (product.recyclingChargeBaseUnit === 0) {
      return null;
   }

   const conversion =
      isDistributionPack(product.unit) && typeof product.conversionFactor === "number" ? product.conversionFactor : 1;
   const recyclingCharge = product.recyclingChargeBaseUnit * conversion;

   return <small>+ {formatPrice(recyclingCharge)} pant</small>;
});

type MiniCartItemProps = {
   item: CartItem;
   onCartChanged: () => void;
   listInformation: GAListInformation;
};

const MiniCartItem = observer(({ item, onCartChanged, listInformation }: MiniCartItemProps) => {
   const itemPosition = cartStore.items.findIndex((i) => i.sku === item.sku);
   const isMobile = useMediaQuery(mobileBreakpoint);

   const handleInputStepperChange = action((newQty: number) => {
      if (cartStore.isEditing()) {
         const validRange = cartStore.getValidQtyRange(item.sku);
         item.qty = clamp(newQty, validRange.min, validRange.max);

         if (!inRangeInclusive(newQty, validRange)) {
            toastStore.addError(
               "Begrensninger på ordren",
               `Ordren som du redigerer har begrensninger som gjør at du ikke kan endre antallet på denne varen til ${newQty}.`,
               { context: "orders", text: "change_order_error" }
            );
         }
      } else {
         const oldQty = item.qty;
         item.qty = newQty;

         if (oldQty !== newQty) {
            cartTrackingStore.startOrUpdateQtyChange({
               sku: item.sku,
               originalQty: oldQty,
               newQty,
               position: itemPosition + 1,
               listInformation
            });
         }
      }
      cartStore.saveCartToServer();
      cartStore.updateCartDependencies();
      onCartChanged();
      return newQty;
   });

   const product = productStore.resolveSku(item.sku);
   if (isUndefined(product)) {
      logger.warn("cart", `Customer has unavailable product in cart with sku ${item.sku}`);
      cartStore.removeFromCart(item.sku);
      return null;
   }

   const removeFromCart = () => {
      sendRemoveFromCart([{ sku: item.sku, qty: item.qty, position: itemPosition + 1 }], listInformation);
      cartStore.removeFromCart(item.sku);
      onCartChanged();
   };

   const removeFromCartButton = (
      <Button
         icon={<IconBin width={24} height={24} />}
         variant="tertiary"
         onClick={removeFromCart}
         aria-label="Fjern vare fra handlekurv"
      />
   );

   const displayTotalPrice = (
      <DisplayPrice
         className="min-w-[140px] text-right"
         price={item.qty * defaultTo(product.priceBeforeDiscount, 0)}
         newPrice={item.qty * defaultTo(product.price, 0) - promotionStore.getPromotionDiscount(item.sku)}
      />
   );

   const displayUnitPrice = (
      <span className="text-sm font-bold">
         {formatPrice(product.price)} pr {formatUnit(product.unit)}
      </span>
   );

   const hasPromotions = promotionStore.isOrderTypeWithPromotions() && promotionStore.getPromotionsBySku(item.sku).length > 0;

   return (
      <Group align="start" justify="start" className="w-full pr-4 my-4 sm:pr-2" gap={2}>
         <ProductImage product={product} size={48} />

         <Stack align="between" className="w-full">
            <Stack align={"between"} gap={1}>
               <Group justify="between">
                  <Link to={productStore.getUrl(product)}>{product.name}</Link>
                  {!isMobile && removeFromCartButton}
               </Group>
               {(product.labels.length > 0 || hasPromotions) && (
                  <Group justify={"start"} gap={2} wrap="wrap">
                     {hasPromotions && <DiscountLabel sku={item.sku} size="regular" />}
                     {product.labels.map((label, index) => (
                        <LabelComponent key={`${label.text}-${index}`} label={label} size="regular" className="inline-block" />
                     ))}
                  </Group>
               )}
            </Stack>
            <Group justify="between" gap={2} className="sm:flex-col sm:items-start">
               <Group gap={2}>{isMobile ? displayUnitPrice : <span className="text-sm">Art. nr: {item.sku}</span>}</Group>

               {isMobile ? displayTotalPrice : displayUnitPrice}
            </Group>
            <RecyclingChange product={product} />
            <LeadTimeWarning product={product} />
            <Group justify="between">
               <InputStepper min={1} value={item.qty} onChange={handleInputStepperChange} />
               {isMobile ? removeFromCartButton : displayTotalPrice}
            </Group>
         </Stack>
      </Group>
   );
});

type MiniCartOrderLines = {
   items: CartItem[];
};

const MiniCartOrderLines = ({ items }: MiniCartOrderLines) => {
   const qtySum = items.reduce((p, i) => p + i.qty, 0);
   const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);
   const addToFavoriteText = "Lagre handlekurven som favorittliste";
   const favoriteAddedText = "Lagret som favorittliste";
   const [favoriteListAdded, setFavoriteListAdded] = useState(false);
   const listInformation: GAListInformation = { type: LIST_TYPE.CART, name: "Minicart", length: items.length };

   const heartIcon = favoriteListAdded ? <IconHeartFilled className="text-[--dsp-color-ink-favorites]" /> : <IconHeart />;
   return (
      <>
         <div className={cn("text-xxs mt-6 @[575px]:px-2 mb-4")}>
            {qtySum} produkter | {items.length} varelinjer
         </div>
         <Divider margin="none" />
         <ul className={listStyle}>
            {items.map((ol, _i) => (
               <li key={ol.sku}>
                  <MiniCartItem item={ol} onCartChanged={() => setFavoriteListAdded(false)} listInformation={listInformation} />
               </li>
            ))}
         </ul>
         <Divider margin="none" />

         <Stack align="start" className="[&>button>svg]:hover:text-[--dsp-color-ink-favorites] mt-4">
            <Button variant="secondary" icon={heartIcon} iconPosition="left" onClick={() => setFavoriteModalOpen(true)}>
               {favoriteListAdded ? favoriteAddedText : addToFavoriteText}
            </Button>
         </Stack>

         <AddToFavoriteModal
            isOpen={favoriteModalOpen}
            onCloseModal={() => setFavoriteModalOpen(false)}
            favoriteListAdded={() => {
               setFavoriteListAdded(true);
               sendAddCartToFavoriteList("minicart");
            }}
         />
      </>
   );
};

export default observer(MiniCartOrderLines);
